import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Alert, AlertTitle, Button, Stack } from '@mui/material';
import { AppContext } from '../../context';
import { ACCESS_TOKEN_KEY } from '../../contants/config';
import { SESSION_EXPIRED_EVENT } from '../../utils/session';

/**
 * One notice instead of a stream of "Unauthorized" toasts when the login runs out while
 * someone is working. It keeps the page as it is, so unsaved edits survive: log in again in
 * a new tab, and the fresh token (same localStorage) is picked up here — the `storage`
 * event closes the notice, the next Save goes through.
 */
export const SessionExpiredNotice: FC = () => {
  const { account, getMe } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  // Read inside the window listeners, which are registered once
  const live = useRef({ account, getMe, open });
  live.current = { account, getMe, open };

  // A login in this tab (account appears or changes) settles it as well
  useEffect(() => {
    setOpen(false);
  }, [account?.id]);

  useEffect(() => {
    // Ignored without an account: opening the app with an old token makes the router's
    // me() answer 401 before anyone is logged in — that must not surface after the login
    const onExpired = () => {
      if (live.current.account) setOpen(true);
    };
    // Fires in this tab when another tab writes the token, i.e. after logging in there;
    // re-read the account so this tab follows whoever logged in
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACCESS_TOKEN_KEY || !e.newValue || !live.current.open) return;
      setOpen(false);
      live.current.getMe?.();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // On the login page (no account) there is nothing to protect — the page itself is the answer
  if (!open || !account) return null;

  return (
    <Alert
      severity="warning"
      onClose={() => setOpen(false)}
      sx={{
        position: 'fixed',
        // Below the fixed file-name / actions bar (ends at 92 px), so it covers neither
        top: 100,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2000,
        width: 'min(640px, calc(100% - 32px))',
        boxShadow: 3,
      }}
    >
      <AlertTitle>Anmeldung abgelaufen</AlertTitle>
      Ein Login gilt 100 Stunden. Ungespeicherte Änderungen bleiben erhalten: in einem neuen Tab neu
      anmelden (dort oben rechts „Login“), dann hier erneut speichern. „Neu laden“ verwirft sie.
      <Stack direction="row" spacing={8} sx={{ mt: 10 }}>
        <Button
          variant="contained"
          color="warning"
          size="small"
          onClick={() => window.open(`${window.location.origin}/`, '_blank', 'noopener')}
        >
          In neuem Tab anmelden
        </Button>
        <Button color="inherit" size="small" onClick={() => window.location.reload()}>
          Neu laden
        </Button>
      </Stack>
    </Alert>
  );
};

export default SessionExpiredNotice;
